import React, { useState, useEffect, useRef } from 'react';
import { Globe, CheckCircle, AlertTriangle, Clock, ExternalLink, Unlink, RefreshCw } from 'lucide-react';
import { GlassButton, GlassCard } from '../ui';
import {
    connectSiteCustomDomain,
    disconnectSiteCustomDomain,
    getSiteCustomDomain,
    SiteCustomDomain,
} from '../../services/api';
import SeoFilesPanel from './SeoFilesPanel';

interface Props {
    siteId: string;
    siteName: string;
}

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--panel-strong)',
    border: '1px solid var(--stroke)',
    borderRadius: 6,
    color: 'var(--text)',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 4,
    display: 'block',
};

const AimeHostedConnectPanel: React.FC<Props> = ({ siteId, siteName }) => {
    const [data, setData]           = useState<SiteCustomDomain | null>(null);
    const [loading, setLoading]     = useState(true);
    const [saving, setSaving]       = useState(false);
    const [error, setError]         = useState<string | null>(null);
    const [domainInput, setDomainInput] = useState('');
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const isConnected = !!data?.connected;
    const isActive    = data?.ssl_live || data?.status === 'active';
    const needsPoll   = isConnected && !isActive;

    useEffect(() => {
        load();
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (needsPoll) {
            pollRef.current = setInterval(load, 30_000);
        }
    }, [needsPoll]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = async () => {
        try {
            const res = await getSiteCustomDomain(siteId);
            setData(res);
        } catch { /* non-fatal */ }
        finally { setLoading(false); }
    };

    const handleConnect = async () => {
        const domain = domainInput.trim();
        if (!domain) { setError('Enter a domain name'); return; }
        setSaving(true);
        setError(null);
        try {
            const res = await connectSiteCustomDomain(siteId, domain);
            setData({ ...res, connected: true });
            setDomainInput('');
            // Reload full status after a brief moment
            setTimeout(load, 1000);
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to connect domain');
        } finally { setSaving(false); }
    };

    const handleDisconnect = async () => {
        if (!window.confirm(`Disconnect ${data?.domain}? The site will no longer be served on that domain.`)) return;
        setSaving(true);
        setError(null);
        try {
            await disconnectSiteCustomDomain(siteId);
            setData({ connected: false });
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Disconnect failed');
        } finally { setSaving(false); }
    };

    const handleRefresh = async () => {
        setLoading(true);
        await load();
    };

    if (loading) {
        return (
            <GlassCard style={{ padding: 16 }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Loading domain settings…</div>
            </GlassCard>
        );
    }

    // Status badge
    let statusBadge: React.ReactNode = null;
    if (isConnected) {
        if (isActive) {
            statusBadge = (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--success, #16a34a)' }}>
                    <CheckCircle size={14} />
                    <span>Live at </span>
                    <a href={`https://${data!.domain}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-500)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        https://{data!.domain} <ExternalLink size={11} />
                    </a>
                </div>
            );
        } else if (data?.dns_points_to_aime) {
            statusBadge = (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--warning, #b45309)' }}>
                    <Clock size={14} />
                    DNS resolved — SSL will provision on first visitor request. Checking every 30 s…
                </div>
            );
        } else if (data?.dns_resolves) {
            statusBadge = (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--danger, #dc2626)' }}>
                    <AlertTriangle size={14} />
                    DNS not yet pointing to AIME — add an A record pointing <strong style={{ margin: '0 3px' }}>{data?.domain}</strong> to <code style={{ background: 'var(--panel-strong)', padding: '1px 5px', borderRadius: 3 }}>{data?.dns_target_ip || 'your AIME server IP'}</code>
                </div>
            );
        } else {
            statusBadge = (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--danger, #dc2626)' }}>
                    <AlertTriangle size={14} />
                    DNS not yet pointing to AIME — add an A record pointing <strong style={{ margin: '0 3px' }}>{data?.domain}</strong> to <code style={{ background: 'var(--panel-strong)', padding: '1px 5px', borderRadius: 3 }}>{data?.dns_target_ip || 'your AIME server IP'}</code>
                </div>
            );
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <GlassCard style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Globe size={18} style={{ color: 'var(--primary-500)' }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)' }}>AIME-Hosted Domain</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        Serve {siteName} on your own domain — no external hosting required
                    </div>
                </div>
                {isConnected && (
                    <GlassButton variant="secondary" onClick={handleRefresh} disabled={loading} style={{ padding: '4px 8px' }}>
                        <RefreshCw size={13} />
                    </GlassButton>
                )}
            </div>

            {/* Error */}
            {error && (
                <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, color: 'var(--danger, #dc2626)', fontSize: '0.875rem' }}>
                    {error}
                </div>
            )}

            {/* Status badge (when connected) */}
            {statusBadge}

            {/* Connected state: show domain + disconnect */}
            {isConnected ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--panel-strong)', borderRadius: 8, border: '1px solid var(--stroke)' }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text)' }}>{data!.domain}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                            Status: {data?.status || 'pending'} · SSL: {data?.ssl_status || 'pending'}
                        </div>
                    </div>
                    <GlassButton variant="secondary" onClick={handleDisconnect} disabled={saving}>
                        <Unlink size={13} />
                    </GlassButton>
                </div>
            ) : (
                /* Not connected: domain input form */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <label style={labelStyle}>Your Domain</label>
                        <input
                            style={inputStyle}
                            value={domainInput}
                            onChange={e => setDomainInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleConnect(); }}
                            placeholder="e.g. www.yourbrand.com"
                            autoComplete="off"
                        />
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                            After connecting, point your domain's A record to{' '}
                            <code style={{ background: 'var(--panel-strong)', padding: '1px 5px', borderRadius: 3 }}>
                                {data?.dns_target_ip || 'your AIME server IP'}
                            </code>
                            . SSL will provision automatically via Let's Encrypt on first request.
                        </div>
                    </div>
                    <GlassButton variant="primary" onClick={handleConnect} disabled={saving} style={{ alignSelf: 'flex-start' }}>
                        {saving ? 'Connecting…' : 'Connect domain'}
                    </GlassButton>
                </div>
            )}
        </GlassCard>

        {isActive && data?.domain_id && data?.domain && (
            <SeoFilesPanel domainId={data.domain_id} domain={data.domain} />
        )}
        </div>
    );
};

export default AimeHostedConnectPanel;
